// @flow
import React, {Component} from 'react'
import { format } from 'd3-format'
import { ChartCanvas, Chart } from 'react-stockcharts'
import {
  BarSeries,
  CandlestickSeries,
  LineSeries,
  MACDSeries,
  SARSeries
} from 'react-stockcharts/lib/series'
import { timeFormat } from 'd3-time-format'
import {
  EdgeIndicator,
  CurrentCoordinate,
  MouseCoordinateX,
  MouseCoordinateY
} from 'react-stockcharts/lib/coordinates'
import { XAxis, YAxis } from 'react-stockcharts/lib/axes'
import { discontinuousTimeScaleProvider } from 'react-stockcharts/lib/scale'
import { fitWidth } from 'react-stockcharts/lib/helper'
import { last } from 'react-stockcharts/lib/utils'
import {
  OHLCTooltip,
  MovingAverageTooltip,
  MACDTooltip,
  SingleValueTooltip
} from 'react-stockcharts/lib/tooltip'
import { ema, macd, sma, sar } from 'react-stockcharts/lib/indicator'

type Props = {
  data: Object[],
  width: number,
  ratio: number,
  indicators: string[],
  type: 'svg' | 'hybrid'
}

const macdAppearance = {
  stroke: {
    macd: '#FF0000',
    signal: '#00F300',
  },
  fill: {
    divergence: '#4682B4'
  }
}

const mouseEdgeAppearance = {
  textFill: '#542605',
  stroke: '#05233B',
  strokeOpacity: 1,
  strokeWidth: 3,
  arrowWidth: 5,
  fill: '#BCDEFA'
}

const accelerationFactor = .02
const maxAccelerationFactor = .2

class CandleStickStockScaleChartWithVolumeBarV1 extends Component<Props> {

  render() {

    const {
      type,
      data: initialData,
      width,
      ratio,
      indicators=[] 
    } = this.props

    const ema26 = ema()
      .id(0)
      .options({ windowSize: 26 })
      .merge((d, c) => { d.ema26 = c })
      .accessor(d => d.ema26)

    const ema12 = ema()
      .id(1)
      .options({ windowSize: 12 })
      .merge((d, c) => { d.ema12 = c })
      .accessor(d => d.ema12)

    const macdCalculator = macd()
      .options({
        fast: 12,
        slow: 26,
        signal: 9
      })
      .merge((d, c) => { d.macd = c })
      .accessor(d => d.macd)

    const smaVolume50 = sma()
      .id(3)
      .options({
        windowSize: 50,
        sourcePath: 'volume'
      })
      .merge((d, c) => { d.smaVolume50 = c })
      .accessor(d => d.smaVolume50)

    const defaultSar = sar()
      .options({
        accelerationFactor, maxAccelerationFactor
      })
      .merge((d, c) => { d.sar = c })
      .accessor(d => d.sar)

    let calculatedData = initialData

    let height = 450
    if(indicators.indexOf('MACD')!==-1) {
      calculatedData = macdCalculator(calculatedData)
      height += 150
    }
    if(indicators.indexOf('EMA')!==-1) {
      calculatedData = ema12(ema26(calculatedData))
    }
    if(indicators.indexOf('SMA')!==-1) {
      calculatedData = smaVolume50(calculatedData)
    }
    if(indicators.indexOf('SAR')!==-1) {
      calculatedData = defaultSar(calculatedData)
    }

    const xScaleProvider = discontinuousTimeScaleProvider
      .inputDateAccessor(d => d.date)

    const {
      data,
      xScale,
      xAccessor,
      displayXAccessor
    } = xScaleProvider(calculatedData)

    const start = xAccessor(last(data))
    const end = xAccessor(data[Math.max(0, data.length - 100)])
    const xExtents = [start, end]

    return (
      <ChartCanvas height={height}
                   ratio={ratio}
                   width={width}
                   margin={{ left: 50, right: 50, top: 10, bottom: 30 }}
                   type={type}
                   seriesName='MSFT'
                   data={data}
                   xScale={xScale}
                   xAccessor={xAccessor}
                   displayXAccessor={displayXAccessor}
                   xExtents={xExtents}>

        <Chart id={1} height={300}
               yExtents={d => [d.high, d.low]}
               padding={{ top: 10, bottom: 20 }}>

          <XAxis axisAt='bottom' orient='bottom'/>
          <YAxis axisAt='right' orient='right' ticks={5} />
          <CandlestickSeries />

          {indicators.includes('EMA') && <LineSeries yAccessor={ema26.accessor()} stroke={ema26.stroke()}/> }
          {indicators.includes('EMA') && <LineSeries yAccessor={ema12.accessor()} stroke={ema12.stroke()}/> }

          {indicators.includes('EMA') &&<CurrentCoordinate yAccessor={ema26.accessor()} fill={ema26.stroke()} />}
          {indicators.includes('EMA') &&<CurrentCoordinate yAccessor={ema12.accessor()} fill={ema12.stroke()} />}

          <EdgeIndicator itemType='last' orient='right' edgeAt='right'
                         yAccessor={d => d.close}
                         fill={d => d.close > d.open ? '#A2F5BF' : '#F9ACAA'}
                         stroke={d => d.close > d.open ? '#0B4228' : '#6A1B19'}
                         textFill={d => d.close > d.open ? '#0B4228' : '#420806'}
                         strokeOpacity={1}
                         strokeWidth={3}
                         arrowWidth={2}/>

          {indicators.includes('EMA') &&<OHLCTooltip origin={[-40, 0]}/>}
          {indicators.includes('EMA') && <MovingAverageTooltip
            onClick={e => console.log(e)}
            origin={[-38, 15]}
            options={[
              {
                yAccessor: ema26.accessor(),
                type: 'EMA',
                stroke: ema26.stroke(),
                windowSize: ema26.options().windowSize,
              },
              {
                yAccessor: ema12.accessor(),
                type: 'EMA',
                stroke: ema12.stroke(),
                windowSize: ema12.options().windowSize,
              },
            ]}
          />}

          {indicators.includes('SAR') && <SARSeries yAccessor={d => d.sar}/>}
          {indicators.includes('SAR') && <MouseCoordinateX
            at='bottom'
            orient='bottom'
            displayFormat={timeFormat('%Y-%m-%d')} />
         }
          {indicators.includes('SAR') && <MouseCoordinateY
            at='right'
            orient='right'
            displayFormat={format('.2f')} />}
          {indicators.includes('SAR') && <SingleValueTooltip
            yLabel={`SAR (${accelerationFactor}, ${maxAccelerationFactor})`}
            yAccessor={d => d.sar}
            origin={[-40, 20]}/>}
        </Chart>
        <Chart id={2} height={150}
               yExtents={[d => d.volume]}
               origin={(w, h) => [0, h - 300]}>

          <YAxis axisAt='left' orient='left' ticks={5} tickFormat={format('.2s')} />
          <BarSeries yAccessor={d => d.volume} />
        </Chart>
        {indicators.includes('MACD') &&
        <Chart id={3} height={150}
            yExtents={macdCalculator.accessor()}
            origin={(w, h) => [0, h - 150]} padding={{top: 10, bottom: 10}}>

          <XAxis axisAt='bottom' orient='bottom' />
          <YAxis axisAt='right' orient='right' ticks={2} />

          <MouseCoordinateX
            at='bottom'
            orient='bottom'
            displayFormat={timeFormat('%Y-%m-%d')}
            rectRadius={5}
            {...mouseEdgeAppearance} />

          <MouseCoordinateY
            at='right'
            orient='right'
            displayFormat={format('.2f')}
            {...mouseEdgeAppearance} />

          <MACDSeries yAccessor={d => d.macd} {...macdAppearance} />
          <MACDTooltip
            origin={[-38, 15]}
            yAccessor={d => d.macd}
            options={macdCalculator.options()}
            appearance={macdAppearance} />

        </Chart>
        }
      </ChartCanvas>
    )
  }
}

CandleStickStockScaleChartWithVolumeBarV1 = fitWidth(CandleStickStockScaleChartWithVolumeBarV1)

export default CandleStickStockScaleChartWithVolumeBarV1



// WEBPACK FOOTER //
// ./src/components/view-chart/chart.js